import moment from 'moment';

import apiClient from '@shared/lib/general/apiClient';
import {
  DEVICE_DETAIL_SET_DEVICE,
  DEVICE_DETAIL_RESET_STATE,
  DEVICE_DETAIL_CHANGE_VALUE,
  DEVICE_DETAIL_CHANGE_VALUES,
  DEVICE_DETAIL_SET_GRAPH_DATA,
  DEVICE_DETAIL_SET_QUERY,
} from '@shared/store/constants/mutationTypes';
import { MEMBER_SUPPORT_SET_MEMBER } from '@/store/constants/mutationTypes';

async function setMemberUids(customerId, commit) {
  try {
    const { data: hubList } = await apiClient({
      method: 'GET',
      url: `/devices/hub/customer/${customerId}`,
    });
    const uidList = hubList.map((d) => ({
      label: d.uid,
      value: d.uid,
    }));
    commit({
      type: DEVICE_DETAIL_CHANGE_VALUE,
      key: 'memberUids',
      value: uidList,
    });
  } catch (err) {
    console.error(err);
  }
}

const actions = {
  async getRawDeviceData({ commit, state }) {
    try {
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingRawDataTab',
        value: true,
      });
      const { currentUid, startDate, endDate } = state;

      let url = `/devices/data/uid/${currentUid}?limit=15`;
      if (startDate && endDate) {
        url = `/devices/data/uid/${currentUid}?start=${moment.utc(startDate).format()}&end=${moment.utc(endDate).format()}`;
      }

      const { data } = await apiClient({
        method: 'GET',
        url,
      });
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'deviceData',
        value: data,
      });
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingRawDataTab',
        value: false,
      });
    } catch (error) {
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingRawDataTab',
        value: false,
      });
      throw error;
    }
  },
  async getDevicePayloadData({ commit, state }) {
    try {
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingSensorDataTab',
        value: true,
      });
      const { currentUid: uid } = state;
      const { data } = await apiClient({
        method: 'GET',
        url: `/devices/data/payload/${uid}?limit=5`,
      });

      const payloadDates = [];

      const readingsBySensor = {};
      Object.keys(data).forEach((key) => {
        const readings = data[key];
        payloadDates.push(key);
        if (readings) {
          Object.keys(readings).forEach((serial) => {
            if (readingsBySensor[serial]) {
              const existingReadings = readingsBySensor[serial];
              readingsBySensor[serial] = [...existingReadings, readings[serial]];
            } else {
              readingsBySensor[serial] = [readings[serial]];
            }
          });
        }
      });

      const sensorReadings = Object.entries(readingsBySensor).map((e) => e[1]).sort((a, b) => {
        const aSn = a[0] && a[0].serialNumber && Number(a[0].serialNumber.slice(-1));
        const bSn = b[0] && b[0].serialNumber && Number(b[0].serialNumber.slice(-1));
        return aSn - bSn;
      });

      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'sensorPayloadData',
        value: sensorReadings,
      });
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'payloadDates',
        value: payloadDates,
      });
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingSensorDataTab',
        value: false,
      });
    } catch (err) {
      console.error(err);
    }
  },
  async getDeviceTimepointData({ commit, state }) {
    try {
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingSensorDataTab',
        value: true,
      });
      const { currentUid: uid } = state;
      const { data } = await apiClient({
        method: 'GET',
        url: `/devices/data/timepoint/${uid}?limit=5`,
      });

      const timepointData = [];
      Object.keys(data).forEach((key) => {
        const readings = data[key];
        const deviceId = key.slice(-1);
        const sensorType = deviceId === '0' ? 'GW_TempHumidity' : '';
        if (readings && readings.length < 5) {
          const iteration = 5 - readings.length;
          for (let i = 0; i < iteration; i += 1) {
            readings.push({
              uploadTime: null,
              temp: 'Not available',
              humidity: 'Not available',
              battery: 'Not available',
              did: deviceId,
              sensorType,
            });
          }
        }
        if (readings && readings.length > 5) {
          readings.length = 5;
        }
        timepointData.push(readings);
      });

      timepointData.sort((a, b) => {
        const aSn = a[0] && a[0].serialNumber && Number(a[0].serialNumber.slice(-1));
        const bSn = b[0] && b[0].serialNumber && Number(b[0].serialNumber.slice(-1));
        return aSn - bSn;
      });
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'sensorTimepointData',
        value: timepointData,
      });
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingSensorDataTab',
        value: false,
      });
    } catch (err) {
      console.error(err);
    }
  },
  async getMigrationList({}, UID) {
    const { data } = await apiClient({
      method: 'GET',
      url: `/migration/list/${UID}`,
    });
    return data;
  },
  async createMigration({ commit }, payload) {
    try {
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'processingMigration',
        value: true,
      });
      const { data } = await apiClient({
        method: 'POST',
        url: '/migration/create/list',
        data: payload,
      });

      if (data) {
        commit('SET_SNACKBAR', {
          show: true,
          color: 'success',
          message: 'Success',
        });
      }
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'processingMigration',
        value: false,
      });
    } catch (error) {
      commit('SET_SNACKBAR', {
        show: true,
        color: 'error',
        message: error,
      });
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'processingMigration',
        value: false,
      });
      throw error;
    }
  },
  changeDeviceDetailValue({ commit }, payload) {
    const { key, value } = payload;
    commit({
      type: DEVICE_DETAIL_CHANGE_VALUE,
      key,
      value,
    });
  },
  setDeviceDetailQuery({ commit }, payload) {
    const {
      uid,
      activeTab,
    } = payload;
    commit({
      type: DEVICE_DETAIL_SET_QUERY,
      uid,
      activeTab,
    });
  },
  async setDetailDevice({ commit, state }, payload) {
    try {
      const { uid, days } = payload;
      const { date } = state;
      const cleanedUId = uid.trim();

      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingDeviceDetail',
        value: true,
      });

      const daysToSub = days || date;
      const endDate = moment.utc();
      const startDate = moment.utc().subtract(daysToSub, 'days');

      const { data } = await apiClient({
        method: 'GET',
        url: `/device/uid/${cleanedUId}?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
      });

      const sensorStatusList = data.deviceStatus?.sort((a, b) => Number(a.did) - Number(b.did));

      const deviceSensorList = data.deviceSensors?.sort((a, b) => Number(a.deviceId) - Number(b.deviceId));

      const hubStatus = sensorStatusList?.find((s) => s.did === '0') || { did: '0', type: 'GW_TempHumidity' };

      let firmware = 'legacy';
      if (data?.deviceConfig && data?.deviceConfig.FW && data?.deviceConfig.FW.Ver) {
        const v = data.deviceConfig.FW.Ver.split('.');
        if (v.length) {
          const vNumber = Number(v[0]);
          if (vNumber < 2 || vNumber > 3) {
            firmware = 'new';
          }
        }
      } else if (hubStatus?.fwVersion) {
        const v = hubStatus.fwVersion.split('.');
        if (v.length) {
          const vNumber = Number(v[0]);
          if (vNumber < 2 || vNumber > 3) {
            firmware = 'new';
          }
        }
      }
      commit({
        type: DEVICE_DETAIL_SET_DEVICE,
        currentUid: uid,
        hub: data.deviceHub || {},
        deviceConfig: data.deviceConfig || {},
        rawPayload: data.rawPayload || {},
        rawBatch: data.rawBatch || {},
        lastSensorReadings: data?.lastSensorReadings || {},
        log: data.log || {},
        hubStatus: hubStatus || {},
        firmware,
        moreInfo: data.moreInfo || {},
        hvac: data.deviceAC || {},
        sensors: deviceSensorList || [],
        customer: data.customer || {},
        graphData: data.statusList || [],
        deviceStatus: data.deviceStatus || [],
      });

      commit({
        type: MEMBER_SUPPORT_SET_MEMBER,
        currentUid: uid,
        hub: data.deviceHub || {},
        deviceConfig: data.deviceConfig || {},
        rawPayload: data.rawPayload || {},
        rawBatch: data.rawBatch || {},
        lastSensorReadings: data.lastSensorReadings || {},
        log: data.log || {},
        hubStatus: hubStatus || {},
        firmware,
        moreInfo: data.moreInfo || {},
        hvac: data.deviceAC || {},
        sensors: deviceSensorList?.filter((s) => s.status === 'Created') || [],
        customer: data.customer || {},
        graphData: data.statusList || [],
        deviceStatus: sensorStatusList || [],
        thermostatZoneList: data.thermostatZones || [],
      });

      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingDeviceDetail',
        value: false,
      });
      if (data.customer.id) {
        await setMemberUids(data.customer.id, commit);
      } else {
        commit({
          type: DEVICE_DETAIL_CHANGE_VALUE,
          key: 'memberUids',
          value: [],
        });
      }
    } catch (err) {
      commit('SET_SNACKBAR', {
        show: true,
        header: 'Something went wrong. Try again later',
        message: err,
        status: 'error',
      });
      console.error(err);
    }
  },
  async getDeviceConnectivity({ commit, state }, payload) {
    const {
      currentUid,
    } = state;
    const {
      startTime,
      endTime,
    } = payload;
    const startDate = moment(startTime);
    const endDate = moment(endTime);
    const { data } = await apiClient({
      method: 'GET',
      url: `/device/uid/${currentUid}?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
    });
    commit({
      type: DEVICE_DETAIL_CHANGE_VALUES,
      changes: [{
        field: 'sensors',
        value: data?.deviceSensors,
      }, {
        field: 'graphData',
        value: data?.statusList,
      }, {
        field: 'deviceStatus',
        value: data?.deviceStatus,
      }],
    });
  },
  async getCyclePayload({ commit, state }, payload) {
    try {
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingCycleChart',
        value: true,
      });

      let pStartTime;
      let pEndTime;
      let pUid;
      let selectedSupplySerial;
      if (payload) {
        pStartTime = payload.startTime;
        pEndTime = payload.endTime;
        pUid = payload.uid;
        if (payload.selectedSupply) {
          selectedSupplySerial = payload.selectedSupply;
        }
      }
      const { currentUid, startTime, endTime } = state;

      let startDate = startTime;
      let endDate = endTime;
      if (pStartTime) {
        startDate = pStartTime;
      }
      if (pEndTime) {
        endDate = pEndTime;
      }
      if (!pUid) {
        pUid = currentUid;
      }
      const { data } = await apiClient({
        method: 'GET',
        url: `/payload/query/${pUid}?start=${startDate}&end=${endDate}&cycles=true&toa=true`,
      });

      const {
        cycles: c, weathers: weatherList, zones: zoneMapping, devices,
      } = data;
      const weatherData = {};
      weatherList.forEach((w) => {
        const {
          timestamp,
          temp,
        } = w;

        const hour = moment(timestamp).hour();
        const date = moment(timestamp).date();
        const key = `${date}-${hour}`;
        weatherData[key] = temp;
      });

      Object.keys(c).forEach((key) => {
        if (!selectedSupplySerial && c[key].length > 0) {
          selectedSupplySerial = key;
        }
      });
      if (!selectedSupplySerial) {
        selectedSupplySerial = Object.keys(c)[0];
      }

      const startOfDay = moment(startDate).startOf('day');
      const endOfDay = moment(endDate).endOf('day');

      commit({
        type: DEVICE_DETAIL_SET_GRAPH_DATA,
        startOfDay,
        endOfDay,
        cyclePayloads: data.payloads,
        allSupplySerialCycles: c,
        selectedSupplySerial,
        startTime: startDate,
        endTime: endDate,
        weatherData,
        zoneMapping,
        devices,
      });
    } catch (err) {
      console.error(err);
    } finally {
      commit({
        type: DEVICE_DETAIL_CHANGE_VALUE,
        key: 'loadingCycleChart',
        value: false,
      });
    }
  },
  async getDeviceCommands({ commit, state }) {
    const { currentUid } = state;
    const { data } = await apiClient({
      method: 'GET',
      url: `/devices/command/query/${currentUid}`,
    });
    commit({
      type: DEVICE_DETAIL_CHANGE_VALUE,
      key: 'commands',
      value: data,
    });
  },
  resetDeviceDetail({ commit }) {
    commit({
      type: DEVICE_DETAIL_RESET_STATE,
    });
  },
};

export default actions;
